import React, {useCallback, useEffect} from 'react';

interface Props {
  width: number;
  aspectRatio: number;
  x: number;
  y: number;
  setWidth: (width: number, x: number, y: number) => void;
}

const TOP_LEFT_POINT = 'top_left';
const TOP_RIGHT_POINT = 'top_right';
const BOTTOM_LEFT_POINT = 'bottom_left';
const BOTTOM_RIGHT_POINT = 'bottom_right';

const ImageSvgHandles = React.memo(
  ({width, aspectRatio, x, y, setWidth}: Props) => {
    const [position, setPosition] = React.useState({
      active: false,
      offset: {x: 0, y: 0},
      activeHandle: '',
      width: 0,
    });

    useEffect(() => {
      window.addEventListener('pointerup', handlePointerUp);
      return () => {
        window.removeEventListener('pointerup', handlePointerUp);
      };
    }, []);

    const getPointByCorner = useCallback(
      (corner: string) => {
        if (corner === TOP_LEFT_POINT) {
          return {
            x,
            y,
          };
        }
        if (corner === TOP_RIGHT_POINT) {
          return {
            x: x + width,
            y,
          };
        }
        if (corner === BOTTOM_LEFT_POINT) {
          return {
            x,
            y: y + width / aspectRatio,
          };
        }
        if (corner === BOTTOM_RIGHT_POINT) {
          return {
            x: x + width,
            y: y + width / aspectRatio,
          };
        }
      },
      [width, x, y],
    );

    const handlePointerDown = (handleId: string) => (e: any) => {
      const el = e.target;
      el.setPointerCapture(e.pointerId);
      if (!position.active) {
        setPosition({
          active: true,
          offset: {
            x: e.clientX,
            y: e.clientY,
          },
          activeHandle: handleId,
          width: width,
        });
      }
    };

    const handlePointerMove = (e: any) => {
      const cX = e.clientX;
      const cY = e.clientY;
      if (position.active) {
        const deltaX = position.offset.x - cX;
        const deltaY = position.offset.y - cY;
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          if (position.activeHandle === TOP_RIGHT_POINT) {
            const newWidth = position.width - deltaX / 2;
            const deltaHeight = width / aspectRatio - newWidth / aspectRatio;
            setWidth(newWidth, x, y + deltaHeight);
          }
          if (position.activeHandle === BOTTOM_LEFT_POINT) {
            const newWidth = position.width + deltaX / 2;
            const deltaWidth = width - newWidth;
            setWidth(newWidth, x + deltaWidth, y);
          }
          if (position.activeHandle === TOP_LEFT_POINT) {
            const newWidth = position.width + deltaX / 2;
            const deltaWidth = width - newWidth;
            const deltaHeight = width / aspectRatio - newWidth / aspectRatio;
            setWidth(newWidth, x + deltaWidth, y + deltaHeight);
          }
          if (position.activeHandle === BOTTOM_RIGHT_POINT) {
            const newWidth = position.width - deltaX / 2;
            setWidth(newWidth, x, y);
          }
        } else {
          if (position.activeHandle === TOP_RIGHT_POINT) {
            const newWidth = position.width + deltaY / 2;
            const deltaHeight = width / aspectRatio - newWidth / aspectRatio;
            setWidth(newWidth, x, y + deltaHeight);
          }
          if (position.activeHandle === TOP_LEFT_POINT) {
            const newWidth = position.width + deltaY / 2;
            const deltaWidth = width - newWidth;
            const deltaHeight = width / aspectRatio - newWidth / aspectRatio;
            setWidth(newWidth, x + deltaWidth, y + deltaHeight);
          }
          if (position.activeHandle === BOTTOM_LEFT_POINT) {
            const newWidth = position.width - deltaY / 2;
            const deltaWidth = width - newWidth;
            setWidth(newWidth, x + deltaWidth, y);
          }
          if (position.activeHandle === BOTTOM_RIGHT_POINT) {
            const newWidth = position.width - deltaY / 2;
            setWidth(newWidth, x, y);
          }
        }
      }
    };

    const handlePointerUp = () => {
      setPosition({
        ...position,
        active: false,
        activeHandle: '',
      });
    };

    const getCursorForPoint = (pointName: string) => {
      switch (pointName) {
        case TOP_LEFT_POINT:
        case BOTTOM_RIGHT_POINT:
          return 'nwse-resize';
        case TOP_RIGHT_POINT:
        case BOTTOM_LEFT_POINT:
          return 'nesw-resize';
      }
    };

    return (
      <g onPointerUp={handlePointerUp}>
        {[
          TOP_LEFT_POINT,
          TOP_RIGHT_POINT,
          BOTTOM_LEFT_POINT,
          BOTTOM_RIGHT_POINT,
        ].map((handle: string) => (
          <circle
            cx={getPointByCorner(handle)!.x}
            cy={getPointByCorner(handle)!.y}
            r={4}
            fill="white"
            stroke="#53B483"
            strokeWidth={2}
            key={handle}
            onPointerDown={handlePointerDown(handle)}
            onPointerMove={handlePointerMove}
            cursor={getCursorForPoint(handle)}
          />
        ))}
      </g>
    );
  },
);

export default ImageSvgHandles;
